import { apiClient } from '@/libs/api_client'
import { AxiosResponse, AxiosError } from 'axios'
import useSWR, { SWRConfiguration } from 'swr'
import { logger } from '@magnetar/utils'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

interface FetchConfig {
  method?: HttpMethod
  params?: Record<string, unknown>
  data?: unknown
  headers?: Record<string, string>
  onUnauthorized?: () => void
}

const createUrl = (baseUrl: string, params?: Record<string, unknown>) => {
  if (!params) return baseUrl

  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value != null) {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

const fetcher = async <T>(url: string, token: string | null, config?: FetchConfig): Promise<T> => {
  const { method = 'GET', params, data, headers = {}, onUnauthorized } = config || {}

  // 如果有 token，加入到 headers
  if (token) {
    headers['Authorization'] = token
  }

  try {
    let response: AxiosResponse<T>

    switch (method) {
      default:
      case 'GET':
        response = await apiClient.get(createUrl(url, params), { headers })
        break
      case 'POST':
        response = await apiClient.post(url, data, { params, headers })
        break
      case 'PUT':
        response = await apiClient.put(url, data, { params, headers })
        break
      case 'DELETE':
        response = await apiClient.delete(url, { params, headers })
        break
      case 'PATCH':
        response = await apiClient.patch(url, data, { params, headers })
        break
    }

    // if (!response) {
    //   throw new Error(response.data.message);
    // }

    // const responseData = response.data.data;
    // if (responseData === undefined) {
    //   throw new Error('Response data is undefined');
    // }

    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 404 || error.response?.status === 401) {
        onUnauthorized?.()
      }
      throw error.response?.data || error
    }
    throw error
  }
}

export function useFetch<T>(
  url?: string | null,
  config?: FetchConfig,
  swrOptions?: SWRConfiguration<T>,
) {
  const defaultOptions: SWRConfiguration<T> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 2000,
    errorRetryCount: 0,
  }

  const key = url ? [url, config?.headers?.['Authorization'] ?? null, JSON.stringify(config)] : null

  return useSWR<T>(
    key,
    async ([url, token, configStr]: [string, string | null, string]) => {
      const parsedConfig = configStr ? JSON.parse(configStr) : undefined
      try {
        const result = await fetcher<T>(url, token, parsedConfig)
        logger.log('Fetcher result:', result)
        return result
      } catch (error) {
        logger.error('Fetcher error:', error)
        throw error
      }
    },
    {
      ...defaultOptions,
      ...swrOptions,
    },
  )
}

// 便捷方法
export function useGet<T>(
  url: string | null,
  params?: Record<string, unknown>,
  token?: string,
  onUnauthorized?: () => void,
  swrOptions?: SWRConfiguration<T>,
) {
  const config: FetchConfig = {
    method: 'GET',
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onUnauthorized,
  }
  return useFetch<T>(url, config, swrOptions)
}

export function usePost<T>(
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
  token?: string,
  onUnauthorized?: () => void,
  swrOptions?: SWRConfiguration<T>,
) {
  const config: FetchConfig = {
    method: 'POST',
    params,
    data,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onUnauthorized,
  }
  return useFetch<T>(url, config, swrOptions)
}

export function usePut<T>(
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
  token?: string,
  onUnauthorized?: () => void,
  swrOptions?: SWRConfiguration<T>,
) {
  const config: FetchConfig = {
    method: 'PUT',
    params,
    data,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onUnauthorized,
  }
  return useFetch<T>(url, config, swrOptions)
}

export function useDelete<T>(
  url: string,
  params?: Record<string, unknown>,
  token?: string,
  onUnauthorized?: () => void,
  swrOptions?: SWRConfiguration<T>,
) {
  const config: FetchConfig = {
    method: 'DELETE',
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onUnauthorized,
  }
  return useFetch<T>(url, config, swrOptions)
}
